import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useReveal from "../lib/useReveal";
import Seo from "../components/Seo";
import { SERVICES } from "../lib/services";

export default function Services() {
    useReveal();
    const location = useLocation();

    useEffect(() => {
        if (location.hash) {
            const el = document.getElementById(location.hash.slice(1));
            if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
        } else {
            window.scrollTo(0, 0);
        }
    }, [location]);

    return (
        <main data-testid="services-page" className="pt-40 md:pt-52">
            <Seo
                title="Catering Services — Corporate, Hospital, Event & Party | The Food Gala"
                description="8 premium catering services: corporate meals, hospital nutrition, hostel & canteen, social and corporate event catering, plus personalized per-plate party orders. Tailored programmes for 50 to 5,000+ guests."
                path="/services"
            />
            {/* HEADER */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
                <div className="grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-8 reveal">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-6">
                            Our Repertoire
                        </div>
                        <h1 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-tight">
                            Meal services for every
                            <br />
                            <em className="not-italic text-[#1B4332]">discerning palate.</em>
                        </h1>
                    </div>
                    <div className="md:col-span-4 reveal">
                        <p className="text-lg font-light text-[#1A1A1A]/75 leading-relaxed">
                            From daily corporate programmes to once-in-a-lifetime celebrations — eight
                            curated services, infinite ways to delight.
                        </p>
                    </div>
                </div>
            </section>

            {/* SERVICE LIST */}
            <section className="bg-[#F3F1EC]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
                    <div className="grid gap-16 md:gap-24">
                        {SERVICES.map((s, idx) => {
                            const reverse = idx % 2 === 1;
                            return (
                                <article
                                    id={s.slug}
                                    key={s.slug}
                                    data-testid={`service-block-${s.slug}`}
                                    className="grid md:grid-cols-12 gap-8 md:gap-14 items-center reveal"
                                >
                                    <div className={`md:col-span-7 ${reverse ? "md:order-2" : ""}`}>
                                        <div className="zoom-wrap aspect-[16/10] bg-[#1B4332] relative">
                                            <img
                                                src={s.image}
                                                alt={`${s.name} by The Food Gala — ${s.short}`}
                                                loading="lazy"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>
                                    <div className={`md:col-span-5 ${reverse ? "md:order-1" : ""}`}>
                                        <div className="flex items-center gap-3 mb-5">
                                            <span className="font-display italic text-3xl text-[#C84B31]">
                                                0{idx + 1}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-[0.34em] text-[#1A1A1A]/55">
                                                {s.kind}
                                            </span>
                                        </div>
                                        <h2 className="font-display text-3xl md:text-5xl leading-[1.02] tracking-tight">
                                            {s.name}
                                        </h2>
                                        <p className="mt-5 text-lg font-light text-[#1A1A1A]/75 leading-relaxed">
                                            {s.long}
                                        </p>
                                        <Link
                                            to={`/enquiry?service=${encodeURIComponent(s.name)}`}
                                            data-testid={`service-enquire-${s.slug}`}
                                            className="mt-8 inline-flex items-center gap-3 bg-[#1B4332] text-[#FAF9F6] px-7 py-3 text-[12px] uppercase tracking-[0.26em] hover:bg-[#112d21] hover:-translate-y-[2px] transition-all duration-300"
                                        >
                                            Enquire for this
                                            <ArrowUpRight size={16} />
                                        </Link>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* BOTTOM CTA */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 reveal">
                <div className="border border-[#1B4332]/20 p-10 md:p-16 grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                        <h3 className="font-display text-3xl md:text-5xl leading-[1.02]">
                            Not sure which service fits?
                        </h3>
                        <p className="mt-4 text-lg font-light text-[#1A1A1A]/75">
                            Share your requirement — we'll architect a tailored programme for your team
                            or event.
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/enquiry"
                            data-testid="services-bottom-cta"
                            className="inline-flex items-center gap-3 bg-[#C84B31] text-[#FAF9F6] px-8 py-4 text-[12px] uppercase tracking-[0.26em] hover:bg-[#a13a24] hover:-translate-y-[2px] transition-all duration-300"
                        >
                            Send an Enquiry
                            <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
